import { useComposerSpace } from "@circle-react/components/QuickPostV3/hooks/useComposerSpace";
import { Icon } from "@circle-react/components/shared/Icon";
import { classNames } from "@circle-react/helpers/twMergeWithCN";
import { Button } from "@circleco/compass/components/Button";
import { Typography } from "@circleco/compass/components/Typography";
import { truncate } from "lodash";
import type { ButtonHTMLAttributes } from "react";
import { forwardRef } from "react";

import { t } from "@/i18n-js/instance";

export interface SpaceTriggerProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  asButton?: boolean;
  "data-popup-open"?: string;
  isMd: boolean;
}

export const SpaceTrigger = forwardRef<HTMLButtonElement, SpaceTriggerProps>(
  (
    { asButton, "data-popup-open": dataPopupOpen, className, isMd, ...props },
    ref,
  ) => {
    const { space: selectedSpace } = useComposerSpace();
    const isOpen =
      props["aria-expanded"] === true ||
      props["aria-expanded"] === "true" ||
      dataPopupOpen !== undefined;

    const label = (
      <Typography variant="label-sm" color="secondary" component="span">
        {selectedSpace?.name ? (
          <>
            {t("quick_post_v2.posting_in")}{" "}
            <span
              className={classNames(
                asButton && "inline-block max-w-[105px] truncate align-bottom",
              )}
            >
              <Typography
                variant="label-sm"
                color="primary"
                component="span"
                title={selectedSpace.name}
              >
                {asButton
                  ? selectedSpace.name
                  : truncate(selectedSpace.name, {
                      length: isMd ? 24 : 36,
                    })}
              </Typography>
            </span>
          </>
        ) : (
          <Typography variant="label-sm" color="tertiary" component="span">
            {t("quick_post_v2.choose_a_space")}
          </Typography>
        )}
      </Typography>
    );

    if (asButton) {
      return (
        <Button
          {...props}
          ref={ref}
          variant="outline"
          data-popup-open={dataPopupOpen}
        >
          {label}
        </Button>
      );
    }

    return (
      <button
        ref={ref}
        type="button"
        className={classNames(
          "focus-visible:bg-secondary hover:bg-secondary flex w-full cursor-pointer items-center rounded-md",
          {
            "bg-secondary": isOpen,
            "px-5 py-4": !isMd,
            "px-0 py-0": isMd,
          },
          className,
        )}
        data-popup-open={dataPopupOpen}
        {...props}
      >
        <div
          className={classNames("flex min-w-0 items-center gap-1", {
            "flex-1": !isMd,
          })}
        >
          {label}
        </div>
        <Icon className="!text-dark" type="16-chevron-down" size={16} />
      </button>
    );
  },
);

SpaceTrigger.displayName = "SpaceTrigger";
